/**
 * Orders and POS contracts (03-modules.md §4, 04-data-model.md "orders",
 * 05-api.md "Orders"): the order and its lines as the API shows them, the
 * command bodies, the order board, the table status map, and the `order.*`
 * outbox events.
 *
 * CLAUDE.md rule 1: a client sends items, quantities, modifiers and context
 * only. Line bodies carry product, variant, option and add-on ids; any price a
 * client adds is not part of the schema and is dropped (Zod strips unknown
 * keys). The server resolves prices from the published menu and prices the
 * order with `packages/pricing`; every response carries the server's figures.
 *
 * Quantities are integer milli-units (1000 = one; `numeric(10,3)` in the
 * database); money is integer paise (CLAUDE.md rule 2).
 *
 * The status lists match `packages/domain/src/orders/status.ts` (a test keeps
 * them equal).
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema, pageQuerySchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import {
  nonNegativePaiseSchema,
  paiseSchema,
  percentBasisPointsSchema,
  basisPointsSchema,
} from './money.js';
import { PRICE_CONTEXTS } from './org.js';
import { TAX_RULE_COMPONENTS } from './pricing.js';

// ---------------------------------------------------------------------------
// Statuses

export const ORDER_TYPES = ['dine_in', 'takeaway', 'delivery', 'aggregator'] as const;
export type OrderTypeName = (typeof ORDER_TYPES)[number];

export const ORDER_STATUSES = [
  'draft',
  'pending_acceptance',
  'accepted',
  'open',
  'billed',
  'partially_paid',
  'settled',
  'closed',
  'rejected',
  'cancelled',
] as const;
export type OrderStatusName = (typeof ORDER_STATUSES)[number];

/** Statuses an order is "live" in: on the board, holding its tables. */
export const LIVE_ORDER_STATUSES = [
  'draft',
  'pending_acceptance',
  'accepted',
  'open',
  'billed',
  'partially_paid',
  'settled',
] as const satisfies readonly OrderStatusName[];

export const FULFILMENT_STATUSES = [
  'not_started',
  'in_kitchen',
  'ready',
  'served',
  'picked_up',
  'handed_to_rider',
  'out_for_delivery',
  'delivered',
  'delivery_failed',
  'returned',
] as const;
export type FulfilmentStatusName = (typeof FULFILMENT_STATUSES)[number];

export const LINE_STATUSES = ['pending', 'sent', 'preparing', 'ready', 'served', 'void'] as const;
export type LineStatusName = (typeof LINE_STATUSES)[number];

/**
 * `order.source`: the app or integration the order came in through
 * (04-data-model.md). Staff apps send `pos` or `captain`; `qr`, `app` and `web`
 * are the customer apps (storefront), `swiggy`/`zomato` aggregators.
 */
export const ORDER_SOURCES = [
  'pos',
  'captain',
  'qr',
  'app',
  'web',
  'phone',
  'swiggy',
  'zomato',
  'api',
] as const;
export type OrderSourceTag = (typeof ORDER_SOURCES)[number];

/** Sources a staff member may open an order with (the others come through their modules). */
export const STAFF_ORDER_SOURCES = ['pos', 'captain', 'phone'] as const;

/** Aggregator platforms (`order.external_platform`); also their pricing channel. */
export const EXTERNAL_PLATFORMS = ['swiggy', 'zomato'] as const;

/** Where a reason code applies (`orders.void_reason.applies_to`). */
export const REASON_KINDS = ['void', 'cancel', 'reject', 'reopen', 'complimentary', 'any'] as const;
export type ReasonKind = (typeof REASON_KINDS)[number];

/** Reserved: the cancel reason a merge leaves on its source orders. */
export const MERGED_REASON = 'merged';

export const MAX_ORDER_LINES_PER_CALL = 200;
export const MAX_TAGS_PER_ORDER = 20;
export const MAX_BULK_ORDERS = 100;

// ---------------------------------------------------------------------------
// Shared pieces

/** Integer milli-units; 1000 = one. At most 10,000 of an item per line. */
export const qtyMilliSchema = z.int().min(1).max(10_000_000);
const seatSchema = z.int().min(0).max(999);
const noteSchema = z.string().trim().max(200);
/** A reason code (`void_reason.code`), e.g. `wrong_item`, `guest_left`. */
export const reasonCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_]+$/, { message: 'Lowercase letters, digits and underscores' });
const reasonNoteSchema = z.string().trim().max(500);
export const orderTagSchema = z
  .string()
  .trim()
  .min(1)
  .max(30)
  .regex(/^[\p{L}\p{N} _-]+$/u, { message: 'Letters, digits, spaces, _ and -' });

const addonChoiceSchema = z.object({
  addonId: uuidSchema,
  /** Per one unit of the line; default 1000. */
  qtyMilli: qtyMilliSchema.optional(),
});

const optionIdsSchema = z
  .array(uuidSchema)
  .max(50)
  .refine((v) => new Set(v).size === v.length, { message: 'Each option at most once' });
const addonsSchema = z
  .array(addonChoiceSchema)
  .max(50)
  .refine((v) => new Set(v.map((a) => a.addonId)).size === v.length, {
    message: 'Each add-on at most once',
  });

/**
 * A line as a till, captain or customer app sends it: what was chosen, never a
 * price. `id` is the client's UUIDv7 (offline tills mint it; replaying the same
 * line id on the same order adds it once).
 */
export const orderLineInputSchema = z.object({
  id: uuidSchema.optional(),
  productId: uuidSchema,
  /** Required when the product has variants (Half/Full). */
  variantId: uuidSchema.nullable().optional(),
  qtyMilli: qtyMilliSchema,
  optionIds: optionIdsSchema.optional(),
  addons: addonsSchema.optional(),
  /** Kitchen note ("less spicy"). */
  note: noteSchema.optional(),
  seat: seatSchema.nullable().optional(),
  course: seatSchema.nullable().optional(),
});
export type OrderLineInput = z.output<typeof orderLineInputSchema>;

const linesInputSchema = z
  .array(orderLineInputSchema)
  .min(1)
  .max(MAX_ORDER_LINES_PER_CALL)
  .refine(
    (v) => {
      const ids = v.flatMap((l) => (l.id === undefined ? [] : [l.id]));
      return new Set(ids).size === ids.length;
    },
    { message: 'Each line id at most once' },
  );

// ---------------------------------------------------------------------------
// Command bodies (05-api.md "Orders"; every one needs an Idempotency-Key)

/** `POST {o}/orders`: open an order, optionally with its first lines. */
export const openOrderSchema = z.object({
  /** The client's UUIDv7 (offline tills); opening the same id again returns that order. */
  id: uuidSchema.optional(),
  type: z.enum(ORDER_TYPES),
  /** Default `pos`. */
  source: z.enum(STAFF_ORDER_SOURCES).optional(),
  /** Dine-in: the table(s) the order sits at. Its section is the tables'. */
  tableIds: z
    .array(uuidSchema)
    .max(20)
    .refine((v) => new Set(v).size === v.length, { message: 'Each table at most once' })
    .optional(),
  /** Section for pricing when there is no table (e.g. a dine-in counter order). */
  sectionId: uuidSchema.nullable().optional(),
  covers: seatSchema.optional(),
  customer: z
    .object({
      name: z.string().trim().min(1).max(80).optional(),
      phone: z
        .string()
        .trim()
        .regex(/^\+?\d{6,15}$/, { message: 'Expected a phone number' })
        .optional(),
    })
    .optional(),
  /** Takeaway or delivery slot. */
  scheduledFor: isoTimestampSchema.nullable().optional(),
  /** When the till created it (offline orders). */
  clientCreatedAt: isoTimestampSchema.optional(),
  lines: z.array(orderLineInputSchema).max(MAX_ORDER_LINES_PER_CALL).optional(),
});
export type OpenOrderBody = z.output<typeof openOrderSchema>;

/** `POST {o}/orders/{id}/lines`. */
export const addLinesSchema = z.object({ lines: linesInputSchema });

/**
 * `POST {o}/orders/{id}/lines/{lineId}/update`: quantity, choices or note of a
 * line the kitchen does not have yet; seat and course of any live line.
 */
export const updateLineSchema = z
  .object({
    qtyMilli: qtyMilliSchema.optional(),
    variantId: uuidSchema.nullable().optional(),
    optionIds: optionIdsSchema.optional(),
    addons: addonsSchema.optional(),
    note: noteSchema.nullable().optional(),
    seat: seatSchema.nullable().optional(),
    course: seatSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

/** `POST {o}/orders/{id}/lines/{lineId}/void`: the whole line, or `qtyMilli` of it. */
export const voidLineSchema = z.object({
  reasonCode: reasonCodeSchema,
  note: reasonNoteSchema.optional(),
  qtyMilli: qtyMilliSchema.optional(),
});

/** `POST {o}/orders/{id}/send-to-kitchen`: the named lines, or every line not yet sent. */
export const sendToKitchenSchema = z.object({
  lineIds: z.array(uuidSchema).min(1).max(MAX_ORDER_LINES_PER_CALL).optional(),
  /** The KOT (print job) id, when the till minted it (offline). */
  kotId: uuidSchema.optional(),
});

/**
 * `POST {o}/orders/{id}/discounts`: one of the outlet's discounts, or a manual
 * one. Role limits and the override path are pricing's (`authorizeDiscounts`).
 */
export const applyDiscountSchema = z.union([
  z.object({
    id: uuidSchema.optional(),
    discountId: uuidSchema,
    /** The items of an `item` discount. */
    lineIds: z.array(uuidSchema).min(1).max(MAX_ORDER_LINES_PER_CALL).optional(),
    reason: z.string().trim().max(200).optional(),
  }),
  z.object({
    id: uuidSchema.optional(),
    manual: z.literal(true),
    name: z.string().trim().min(1).max(60).optional(),
    calc: z.literal('percent'),
    rateBp: percentBasisPointsSchema,
    maxPaise: nonNegativePaiseSchema.nullable().optional(),
    lineIds: z.array(uuidSchema).min(1).max(MAX_ORDER_LINES_PER_CALL).optional(),
    reason: z.string().trim().max(200).optional(),
  }),
  z.object({
    id: uuidSchema.optional(),
    manual: z.literal(true),
    name: z.string().trim().min(1).max(60).optional(),
    calc: z.literal('flat'),
    amountPaise: nonNegativePaiseSchema,
    lineIds: z.array(uuidSchema).min(1).max(MAX_ORDER_LINES_PER_CALL).optional(),
    reason: z.string().trim().max(200).optional(),
  }),
]);
export type ApplyDiscountBody = z.output<typeof applyDiscountSchema>;

/** `POST {o}/orders/{id}/discounts/{discountId}/remove`. */
export const removeDiscountSchema = z.object({ reason: z.string().trim().max(200).optional() });

/**
 * `POST {o}/orders/{id}/charges`: which of the outlet's charges this order
 * waives (the full list; the others apply as their rules say).
 */
export const setChargesSchema = z.object({
  waivedChargeIds: z
    .array(uuidSchema)
    .max(50)
    .refine((v) => new Set(v).size === v.length, { message: 'Each charge at most once' }),
});

/** `POST {o}/orders/{id}/complimentary`: the named lines, or the whole order. */
export const complimentarySchema = z
  .object({
    lineIds: z.array(uuidSchema).min(1).max(MAX_ORDER_LINES_PER_CALL).optional(),
    complimentary: z.boolean(),
    reasonCode: reasonCodeSchema.optional(),
  })
  .refine((v) => !v.complimentary || v.reasonCode !== undefined, {
    message: 'A reason code is required',
    path: ['reasonCode'],
  });

/** Body of commands that take nothing (bill, accept). */
export const emptyCommandSchema = z.object({});

/** `POST {o}/orders/{id}/reopen`, `/cancel`, `/reject`. */
export const reasonSchema = z.object({
  reasonCode: reasonCodeSchema,
  note: reasonNoteSchema.optional(),
});

const splitItemSchema = z.object({ lineId: uuidSchema, qtyMilli: qtyMilliSchema.optional() });

/** `POST {o}/orders/{id}/split` (packages/domain `planSplit`). */
export const splitOrderSchema = z.discriminatedUnion('mode', [
  /** Each part becomes a new order with these lines (whole, or `qtyMilli` of one). */
  z.object({
    mode: z.literal('lines'),
    parts: z.array(z.array(splitItemSchema).min(1).max(MAX_ORDER_LINES_PER_CALL)).min(1).max(49),
  }),
  /** One order per seat; unseated lines stay (`keep`) or are shared equally (`even`). */
  z.object({ mode: z.literal('seats'), shared: z.enum(['keep', 'even']).optional() }),
  /** N equal parts. */
  z.object({ mode: z.literal('equal'), parts: z.int().min(2).max(50) }),
  /** Parts by amount (must add up to the order total). */
  z.object({
    mode: z.literal('amounts'),
    amountsPaise: z.array(nonNegativePaiseSchema).min(2).max(50),
  }),
]);
export type SplitOrderBody = z.output<typeof splitOrderSchema>;

/** `POST {o}/orders/merge`: the sources' lines move to the target; the sources end. */
export const mergeOrdersSchema = z.object({
  targetOrderId: uuidSchema,
  sourceOrderIds: z
    .array(uuidSchema)
    .min(1)
    .max(20)
    .refine((v) => new Set(v).size === v.length, { message: 'Each order at most once' }),
});

/** `POST {o}/orders/{id}/move-table`: to other table(s); a change of section re-prices. */
export const moveTableSchema = z.object({
  tableIds: z
    .array(uuidSchema)
    .min(1)
    .max(20)
    .refine((v) => new Set(v).size === v.length, { message: 'Each table at most once' }),
});

/** `POST {o}/orders/{id}/transfer-lines`: items (whole, or part) to another open order. */
export const transferLinesSchema = z.object({
  toOrderId: uuidSchema,
  lines: z.array(splitItemSchema).min(1).max(MAX_ORDER_LINES_PER_CALL),
});

/** `POST {o}/orders/{id}/fulfilment`. */
export const fulfilmentSchema = z.object({ status: z.enum(FULFILMENT_STATUSES) });

/** `POST {o}/orders/{id}/tags`: tags to add and to remove. */
export const tagsSchema = z
  .object({
    add: z.array(orderTagSchema).max(MAX_TAGS_PER_ORDER).optional(),
    remove: z.array(orderTagSchema).max(MAX_TAGS_PER_ORDER).optional(),
  })
  .refine((v) => (v.add?.length ?? 0) + (v.remove?.length ?? 0) > 0, {
    message: 'Add or remove at least one tag',
  });

/** `POST {o}/orders/{id}/assign`: the captain or staff member the order is transferred to. */
export const assignStaffSchema = z.object({ staffId: uuidSchema.nullable() });

/** `POST {o}/orders/{id}/covers`. */
export const coversSchema = z.object({ covers: seatSchema });

/**
 * `POST {o}/orders/bulk`: one action on many orders (`orders.bulk`, and each
 * order's own permission). Each order succeeds or fails on its own.
 */
export const bulkActionSchema = z.object({
  action: z.literal('cancel'),
  orderIds: z
    .array(uuidSchema)
    .min(1)
    .max(MAX_BULK_ORDERS)
    .refine((v) => new Set(v).size === v.length, { message: 'Each order at most once' }),
  reasonCode: reasonCodeSchema,
  note: reasonNoteSchema.optional(),
});

export const orderParamsSchema = z.object({ outletId: uuidSchema, orderId: uuidSchema });
export const orderLineParamsSchema = z.object({
  outletId: uuidSchema,
  orderId: uuidSchema,
  lineId: uuidSchema,
});
export const orderDiscountParamsSchema = z.object({
  outletId: uuidSchema,
  orderId: uuidSchema,
  discountId: uuidSchema,
});

const csv = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .string()
    .max(400)
    .transform((v) =>
      v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== ''),
    )
    .pipe(z.array(z.enum(values)).max(values.length));

/**
 * `GET {o}/orders`: the order board and order search. Without `status` or
 * `businessDate`, the live orders (every live status); with `businessDate`,
 * every order of that day. `q` matches the order number, the customer's name,
 * an aggregator id or a tag.
 */
export const orderListQuerySchema = pageQuerySchema.extend({
  /** Comma-separated statuses. */
  status: csv(ORDER_STATUSES).optional(),
  type: csv(ORDER_TYPES).optional(),
  sectionId: uuidSchema.optional(),
  tableId: uuidSchema.optional(),
  businessDate: businessDateSchema.optional(),
  q: z.string().trim().min(1).max(60).optional(),
});

// ---------------------------------------------------------------------------
// Views

export const orderLineModifierSchema = z.object({
  kind: z.enum(['option', 'addon']),
  refId: uuidSchema,
  name: z.string(),
  /** Per one unit of the line. */
  qtyMilli: z.int(),
  unitPricePaise: nonNegativePaiseSchema,
});

export const orderLineSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  productId: uuidSchema,
  variantId: uuidSchema.nullable(),
  categoryId: uuidSchema.nullable(),
  name: z.string(),
  qtyMilli: z.int(),
  /** As resolved from the published menu when the line was added. */
  unitPricePaise: nonNegativePaiseSchema,
  taxInclusive: z.boolean(),
  taxGroupId: uuidSchema.nullable(),
  /** Pre-tax discount share (`order_line.discount_paise`). */
  discountPaise: paiseSchema,
  taxablePaise: paiseSchema,
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  cessPaise: paiseSchema,
  lineTotalPaise: paiseSchema,
  status: z.enum(LINE_STATUSES),
  complimentary: z.boolean(),
  /** The confirmed KOT that sent it. */
  kotId: uuidSchema.nullable(),
  /** A KOT for it is printing (sent to the kitchen, not yet confirmed). */
  pendingKotId: uuidSchema.nullable(),
  note: z.string().nullable(),
  seat: z.int().nullable(),
  course: z.int().nullable(),
  voidReasonCode: z.string().nullable(),
  voidedAt: isoTimestampSchema.nullable(),
  /** The line this one was split from (lineage root). */
  originLineId: uuidSchema.nullable(),
  /** Rejoined into that line (quantity 0). */
  absorbedIntoLineId: uuidSchema.nullable(),
  addedBy: uuidSchema.nullable(),
  addedAt: isoTimestampSchema,
  modifiers: z.array(orderLineModifierSchema),
});
export type OrderLineView = z.output<typeof orderLineSchema>;

export const orderDiscountSchema = z.object({
  id: uuidSchema,
  /** The outlet's discount; null for a manual one. */
  discountId: uuidSchema.nullable(),
  name: z.string().nullable(),
  scope: z.enum(['order', 'item', 'category']),
  calc: z.enum(['percent', 'flat']),
  rateBp: z.int().nullable(),
  /** Flat discounts: the amount asked for. */
  valuePaise: nonNegativePaiseSchema.nullable(),
  maxPaise: nonNegativePaiseSchema.nullable(),
  lineIds: z.array(uuidSchema),
  categoryIds: z.array(uuidSchema),
  /** As applied by the last pricing (price basis). */
  amountPaise: paiseSchema,
  reason: z.string().nullable(),
  appliedBy: uuidSchema.nullable(),
  /** The manager whose override allowed a discount above the limit. */
  approvedBy: uuidSchema.nullable(),
  appliedAt: isoTimestampSchema,
});

export const orderChargeSchema = z.object({
  otherChargeId: uuidSchema,
  name: z.string(),
  kind: z.enum(['packaging', 'service', 'delivery', 'other']),
  waived: z.boolean(),
  amountPaise: paiseSchema,
  taxPaise: paiseSchema,
});

export const orderTaxSchema = z.object({
  component: z.enum(TAX_RULE_COMPONENTS),
  rateBp: basisPointsSchema,
  taxablePaise: paiseSchema,
  taxPaise: paiseSchema,
});

export const orderSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  businessDate: businessDateSchema,
  /** Per outlet per business date, for display; not the invoice number. */
  orderNo: z.int(),
  type: z.enum(ORDER_TYPES),
  source: z.string(),
  /** The pricing channel. */
  channel: z.enum(PRICE_CONTEXTS),
  status: z.enum(ORDER_STATUSES),
  fulfilmentStatus: z.enum(FULFILMENT_STATUSES),
  sectionId: uuidSchema.nullable(),
  tableIds: z.array(uuidSchema),
  covers: z.int(),
  customer: z.object({ name: z.string().nullable(), phone: z.string().nullable() }).nullable(),
  /** The customers-module customer attached to the order (khata, rewards); null: none. */
  customerId: uuidSchema.nullable().default(null),
  externalPlatform: z.string().nullable(),
  externalId: z.string().nullable(),
  scheduledFor: isoTimestampSchema.nullable(),
  assignedStaffId: uuidSchema.nullable(),
  isComplimentary: z.boolean(),
  splitFromOrderId: uuidSchema.nullable(),
  mergedIntoOrderId: uuidSchema.nullable(),
  subtotalPaise: paiseSchema,
  discountPaise: paiseSchema,
  chargesPaise: paiseSchema,
  taxPaise: paiseSchema,
  roundOffPaise: paiseSchema,
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
  /** `totalPaise - paidPaise`. */
  duePaise: paiseSchema,
  pricingVersion: z.string(),
  /** The server priced an offline bill differently and kept the bill (pricing.discrepancy). */
  pricingDiscrepancy: z
    .object({ keptTotalPaise: paiseSchema, serverTotalPaise: paiseSchema })
    .nullable(),
  invoiceId: uuidSchema.nullable(),
  cancelReasonCode: z.string().nullable(),
  cancelNote: z.string().nullable(),
  tags: z.array(z.string()),
  lines: z.array(orderLineSchema),
  discounts: z.array(orderDiscountSchema),
  charges: z.array(orderChargeSchema),
  taxes: z.array(orderTaxSchema),
  createdBy: uuidSchema.nullable(),
  createdTerminalId: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  billedAt: isoTimestampSchema.nullable(),
  settledAt: isoTimestampSchema.nullable(),
  closedAt: isoTimestampSchema.nullable(),
  cancelledAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export type OrderView = z.output<typeof orderSchema>;

export const orderHistoryEntrySchema = z.object({
  id: uuidSchema,
  field: z.enum(['status', 'fulfilment_status', 'line_status']),
  lineId: uuidSchema.nullable(),
  from: z.string().nullable(),
  to: z.string(),
  reason: z.string().nullable(),
  actorId: uuidSchema.nullable(),
  terminalId: uuidSchema.nullable(),
  at: isoTimestampSchema,
});

/** `GET {o}/orders/{id}`: the order and its status history. */
export const orderDetailSchema = orderSchema.extend({
  history: z.array(orderHistoryEntrySchema),
});

/** Commands touching several orders (split, merge, transfer) answer with each. */
export const ordersResultSchema = z.object({ orders: z.array(orderSchema) });

/** One row of the order board. */
export const orderSummarySchema = z.object({
  id: uuidSchema,
  businessDate: businessDateSchema,
  orderNo: z.int(),
  type: z.enum(ORDER_TYPES),
  source: z.string(),
  channel: z.enum(PRICE_CONTEXTS),
  status: z.enum(ORDER_STATUSES),
  fulfilmentStatus: z.enum(FULFILMENT_STATUSES),
  sectionId: uuidSchema.nullable(),
  tableIds: z.array(uuidSchema),
  covers: z.int(),
  customerName: z.string().nullable(),
  externalId: z.string().nullable(),
  assignedStaffId: uuidSchema.nullable(),
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
  /** Live (not voided) lines. */
  lineCount: z.int(),
  /** Live lines the kitchen does not have yet. */
  unsentLineCount: z.int(),
  tags: z.array(z.string()),
  scheduledFor: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  billedAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export const orderListSchema = z.object({
  data: z.array(orderSummarySchema),
  nextCursor: z.string().nullable(),
});

/** At most this many orders per queue (oldest first); the board pages the rest. */
export const MAX_QUEUE_ORDERS = 200;

/**
 * `GET {o}/orders/queues` (03-modules.md §4 "pending/missing queues"): live orders
 * that need someone at the till, oldest first.
 */
export const orderQueuesSchema = z.object({
  /** QR, online and aggregator orders waiting for the outlet to accept or reject them. */
  awaitingAcceptance: z.array(orderSummarySchema),
  /**
   * Orders with items sent to the kitchen whose KOT has not printed yet (requested,
   * neither confirmed nor failed): a missing KOT when it waits long.
   */
  awaitingKot: z.array(
    orderSummarySchema.extend({
      /** The KOTs requested and not confirmed. */
      pendingKotIds: z.array(uuidSchema),
      /** The oldest last change of those items (normally when they were sent). */
      requestedSince: isoTimestampSchema,
    }),
  ),
  /**
   * Running orders with items the kitchen does not have and nobody asked for: never
   * sent, or sent again after their KOT failed to print.
   */
  unsent: z.array(orderSummarySchema),
});

export const TABLE_STATES = ['free', 'occupied', 'billed', 'out_of_service'] as const;

/** `GET {o}/tables/status`: the table map. */
export const tableStatusListSchema = z.object({
  data: z.array(
    z.object({
      tableId: uuidSchema,
      name: z.string(),
      sectionId: uuidSchema,
      seats: z.int(),
      /** `billed`: every order at it is billed (or paid); `occupied`: some order is running. */
      state: z.enum(TABLE_STATES),
      /** When the earliest live order at the table was opened (for the timer). */
      occupiedSince: isoTimestampSchema.nullable(),
      orders: z.array(
        z.object({
          orderId: uuidSchema,
          orderNo: z.int(),
          status: z.enum(ORDER_STATUSES),
          covers: z.int(),
          totalPaise: paiseSchema,
          openedAt: isoTimestampSchema,
          billedAt: isoTimestampSchema.nullable(),
        }),
      ),
    }),
  ),
});

/** Each order's outcome in a bulk action. */
export const bulkResultSchema = z.object({
  results: z.array(
    z.object({
      orderId: uuidSchema,
      ok: z.boolean(),
      code: z.string().nullable(),
      detail: z.string().nullable(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Events (03-modules.md §4, plus the four in packages/domain README decision 14)

/**
 * What every order event carries: enough for the order board and the table
 * map to update without a refetch. No personal data (it is projected to the
 * outlet-wide pos room).
 */
const orderRef = {
  orderId: uuidSchema,
  orderNo: z.int(),
  businessDate: businessDateSchema,
  type: z.enum(ORDER_TYPES),
  source: z.string(),
  status: z.enum(ORDER_STATUSES),
  fulfilmentStatus: z.enum(FULFILMENT_STATUSES),
  sectionId: uuidSchema.nullable(),
  tableIds: z.array(uuidSchema),
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
  version: z.int(),
};

const kitchenLineSchema = z.object({
  lineId: uuidSchema,
  productId: uuidSchema,
  variantId: uuidSchema.nullable(),
  name: z.string(),
  qtyMilli: z.int(),
  note: z.string().nullable(),
  seat: z.int().nullable(),
  course: z.int().nullable(),
  modifiers: z.array(z.object({ kind: z.string(), name: z.string(), qtyMilli: z.int() })),
});

export const orderPlacedV1 = defineEvent(
  'order.placed',
  1,
  z.object({ ...orderRef, channel: z.enum(PRICE_CONTEXTS), lineCount: z.int() }),
);

export const orderLinesAddedV1 = defineEvent(
  'order.lines_added',
  1,
  z.object({ ...orderRef, lines: z.array(kitchenLineSchema) }),
);

export const orderLineVoidedV1 = defineEvent(
  'order.line_voided',
  1,
  z.object({
    ...orderRef,
    lineId: uuidSchema,
    /** The line it was cut from when part of a line was voided. */
    fromLineId: uuidSchema,
    qtyMilli: z.int(),
    fromStatus: z.enum(LINE_STATUSES),
    reasonCode: z.string(),
    /** The kitchen had it (or was printing it): the kitchen prints a void KOT. */
    wasSent: z.boolean(),
    kotId: uuidSchema.nullable(),
  }),
);

/** Lines to send: the kitchen module creates the KOT and its print job (M3 task 3). */
export const orderKotRequestedV1 = defineEvent(
  'order.kot_requested',
  1,
  z.object({ ...orderRef, kotId: uuidSchema, lines: z.array(kitchenLineSchema) }),
);

/**
 * The server priced an order the till billed offline differently (02-architecture.md
 * Offline): the offline-billed amount stays, the manager is told (F10 step 6).
 */
export const pricingDiscrepancyV1 = defineEvent(
  'pricing.discrepancy',
  1,
  z.object({
    ...orderRef,
    keptTotalPaise: paiseSchema,
    serverTotalPaise: paiseSchema,
    /** Server minus kept: positive when the outlet under-charged. */
    differencePaise: paiseSchema,
    lines: z.array(
      z.object({
        lineId: uuidSchema,
        keptTotalPaise: paiseSchema,
        serverTotalPaise: paiseSchema,
      }),
    ),
  }),
);

export const orderBilledV1 = defineEvent('order.billed', 1, z.object({ ...orderRef }));

export const orderSettledV1 = defineEvent(
  'order.settled',
  1,
  z.object({ ...orderRef, invoiceId: uuidSchema.nullable() }),
);

export const orderCancelledV1 = defineEvent(
  'order.cancelled',
  1,
  z.object({
    ...orderRef,
    fromStatus: z.enum(ORDER_STATUSES),
    reasonCode: z.string(),
    /** Lines the kitchen has (or is printing): print a void KOT for them. */
    sentLineIds: z.array(uuidSchema),
  }),
);

export const orderAcceptedV1 = defineEvent('order.accepted', 1, z.object({ ...orderRef }));

export const orderRejectedV1 = defineEvent(
  'order.rejected',
  1,
  z.object({ ...orderRef, reasonCode: z.string(), refundDuePaise: paiseSchema }),
);

export const orderFulfilmentChangedV1 = defineEvent(
  'order.fulfilment_changed',
  1,
  z.object({
    ...orderRef,
    from: z.enum(FULFILMENT_STATUSES),
    to: z.enum(FULFILMENT_STATUSES),
    auto: z.boolean(),
  }),
);

export const orderMergedV1 = defineEvent(
  'order.merged',
  1,
  z.object({
    ...orderRef,
    role: z.enum(['target', 'source']),
    targetOrderId: uuidSchema,
    sourceOrderIds: z.array(uuidSchema),
  }),
);

export const orderSplitV1 = defineEvent(
  'order.split',
  1,
  z.object({
    ...orderRef,
    role: z.enum(['source', 'part']),
    sourceOrderId: uuidSchema,
    mode: z.enum(['lines', 'seats', 'equal', 'amounts']),
    partOrderIds: z.array(uuidSchema),
  }),
);

export const orderTableMovedV1 = defineEvent(
  'order.table_moved',
  1,
  z.object({
    ...orderRef,
    fromTableIds: z.array(uuidSchema),
    fromSectionId: uuidSchema.nullable(),
  }),
);

export const orderReopenedV1 = defineEvent(
  'order.reopened',
  1,
  z.object({ ...orderRef, reasonCode: z.string() }),
);

export const orderLinesTransferredV1 = defineEvent(
  'order.lines_transferred',
  1,
  z.object({
    ...orderRef,
    fromOrderId: uuidSchema,
    toOrderId: uuidSchema,
    lineIds: z.array(uuidSchema),
  }),
);

export const orderClosedV1 = defineEvent('order.closed', 1, z.object({ ...orderRef }));

/**
 * Smaller changes (line edits, complimentary, discount and charge inputs,
 * kitchen progress, staff, covers, tags, accepted → open): `changes` names them.
 */
export const orderUpdatedV1 = defineEvent(
  'order.updated',
  1,
  z.object({ ...orderRef, changes: z.array(z.string()) }),
);

export const ORDER_EVENTS = [
  orderPlacedV1,
  orderLinesAddedV1,
  orderLineVoidedV1,
  orderKotRequestedV1,
  pricingDiscrepancyV1,
  orderBilledV1,
  orderSettledV1,
  orderCancelledV1,
  orderAcceptedV1,
  orderRejectedV1,
  orderFulfilmentChangedV1,
  orderMergedV1,
  orderSplitV1,
  orderTableMovedV1,
  orderReopenedV1,
  orderLinesTransferredV1,
  orderClosedV1,
  orderUpdatedV1,
] as const;
